import { useEffect, useState } from 'react';
import { api, tokens } from './api/client';
import type { User } from './api/types';
import { LangContext, useT, type Lang } from './i18n';
import { useLiveFeed } from './useLiveFeed';
import { ToastProvider, useTheme, useToast } from './components/Toast';
import { IconBell, IconBellOff, IconMoon, IconSun } from './components/Icons';
import { Logo } from './components/Logo';
import { useDesktopNotifications, useVehicleAlerts } from './useVehicleAlerts';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Live } from './pages/Live';
import { Replay } from './pages/Replay';
import { Admin } from './pages/Admin';

type Tab = 'live' | 'replay' | 'admin';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

function Shell({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const { t, lang, setLang } = useT();
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('live');
  const { devices, loading, error, connected, refresh, offline, clearOffline } = useLiveFeed();

  const [alertsOn, setAlertsOn] = useState(
    () => localStorage.getItem('gps.alerts') !== 'off',
  );
  const { alerts, clear } = useVehicleAlerts(devices, alertsOn);
  const { permission, request, notify } = useDesktopNotifications();

  useEffect(() => {
    localStorage.setItem('gps.alerts', alertsOn ? 'on' : 'off');
  }, [alertsOn]);

  // One place turns a movement change into something the customer actually sees.
  useEffect(() => {
    if (alerts.length === 0) return;
    for (const alert of alerts) {
      const text = t(alert.kind === 'started' ? 'vehicleStarted' : 'vehicleStopped').replace(
        '{name}',
        alert.name,
      );
      toast(alert.kind === 'started' ? 'ok' : 'info', text);
      notify(alert.name, text, `vehicle-${alert.deviceId}`);
    }
    clear();
  }, [alerts, clear, notify, t, toast]);

  // "Stopped reporting" is decided by the server's sweep and arrives over the
  // socket, so the dashboard and the phone always agree on it. It is a warning
  // rather than information: a tracker going quiet may be a basement, or a
  // vehicle being taken somewhere the aerial was cut.
  useEffect(() => {
    if (offline.length === 0 || !alertsOn) return;
    for (const alert of offline) {
      const text = t('vehicleOffline').replace('{name}', alert.name);
      toast('err', text);
      notify(alert.name, text, `offline-${alert.deviceId}`);
    }
    clearOffline();
  }, [offline, alertsOn, clearOffline, notify, t, toast]);

  const toggleAlerts = async () => {
    const next = !alertsOn;
    setAlertsOn(next);
    if (next && permission === 'default') {
      const result = await request();
      if (result === 'denied') toast('info', t('desktopAlertsBlocked'));
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <Logo size={30} />

        <nav className="tab-row">
          <button
            type="button"
            className={tab === 'live' ? 'tab active' : 'tab'}
            onClick={() => setTab('live')}
          >
            {t('live')}
          </button>
          <button
            type="button"
            className={tab === 'replay' ? 'tab active' : 'tab'}
            onClick={() => setTab('replay')}
          >
            {t('replay')}
          </button>
          {user.role === 'admin' && (
            <button
              type="button"
              className={tab === 'admin' ? 'tab active' : 'tab'}
              onClick={() => setTab('admin')}
            >
              {t('admin')}
            </button>
          )}
        </nav>

        <div className="header-right">
          <button
            type="button"
            className={alertsOn ? 'icon-btn active' : 'icon-btn'}
            onClick={toggleAlerts}
            aria-label={alertsOn ? t('notificationsOn') : t('notificationsOff')}
            title={
              alertsOn
                ? permission === 'granted'
                  ? t('notificationsOn')
                  : t('allowDesktopAlerts')
                : t('notificationsOff')
            }
          >
            {alertsOn ? <IconBell /> : <IconBellOff />}
          </button>

          <button
            type="button"
            className="icon-btn"
            onClick={toggle}
            aria-label={theme === 'dark' ? t('lightMode') : t('darkMode')}
            title={theme === 'dark' ? t('lightMode') : t('darkMode')}
          >
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>

          <button
            type="button"
            className="icon-btn"
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            aria-label="language"
            style={{ fontSize: 11, fontWeight: 700 }}
          >
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>

          <div className="user-chip">
            <span className="user-name small">{user.name}</span>
            <span className="avatar">{initials(user.name)}</span>
          </div>

          <button type="button" className="link-btn" onClick={onSignOut}>
            {t('signOut')}
          </button>
        </div>
      </header>

      <main>
        {tab === 'live' && (
          <Live
            devices={devices}
            loading={loading}
            error={error}
            connected={connected}
            onRetry={refresh}
            canClaim={user.role !== 'admin'}
          />
        )}
        {tab === 'replay' && <Replay devices={devices} />}
        {tab === 'admin' && user.role === 'admin' && (
          <Admin devices={devices} onDevicesChanged={refresh} />
        )}
      </main>
    </div>
  );
}

export function App() {
  const [lang, setLang] = useState<Lang>(
    () => (localStorage.getItem('gps.lang') as Lang | null) ?? 'ar',
  );
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [showRegister, setShowRegister] = useState(false);

  // Keep the document in sync so CSS logical properties and fonts behave.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('gps.lang', lang);
  }, [lang]);

  // Restore the session on reload rather than forcing a fresh login.
  useEffect(() => {
    if (!tokens.access) {
      setChecking(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => tokens.clear())
      .finally(() => setChecking(false));
  }, []);

  const signOut = () => {
    api.logout();
    setUser(null);
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <ToastProvider>
        {checking ? (
          <div className="center-note">
            <div className="spinner" />
          </div>
        ) : user ? (
          <Shell user={user} onSignOut={signOut} />
        ) : showRegister ? (
          <Register
            onRegistered={(created) => {
              setShowRegister(false);
              setUser(created);
            }}
            onBackToLogin={() => setShowRegister(false)}
          />
        ) : (
          <Login onSignedIn={setUser} onRegister={() => setShowRegister(true)} />
        )}
      </ToastProvider>
    </LangContext.Provider>
  );
}
